import {
  BUILT_IN_THEME_NAMES,
  THEME_SIZES,
  computeThemeTokens,
  type BuiltInThemeName,
  type ThemeTokens,
  type WeekdayCategorySeries,
} from "@ii-gp-widget/shared/browser";
import { STRINGS, WEEKDAY_ORDER, t, type Locale } from "./i18n";
import { mountAiptTab } from "./aipt";
import { createTracker } from "./analytics";
import { mountFeedbackWidget } from "./feedback";
import {
  parseAipt,
  parseDisplayMode,
  parseFont,
  parseFontSize,
  parseHours,
  parseMaxWidth,
  parsePrivacyUrl,
  type FontMode,
  type FontSize,
  type HourRange,
} from "./embed-options";
import { WIDGET_CSS, fontFaceCss } from "./styles";
import { computeForecastScale } from "./forecast-scale";
import { gymplusLogoSvg, logoTextColor } from "./logo";

// Now tab, Week tab (both drawn as the same SVG curve, no chart library), the chain switcher
// panel, AI-PT (Phase 8, opens as an overlay over the tabs/panels/foot), and the feedback
// widget (Phase 9).

interface AvailabilityCategory {
  key: string;
  label: string;
  objectCount: number;
  estimatedCapacity: number | null;
  groupName?: string;
}

interface HourlyPrediction {
  categoryKey: string;
  offsetHours: number;
  hour: number;
  predictedCount: number;
  outsideTypicalHours: boolean;
}

interface RenderPayload {
  premise: {
    premiseId: number;
    chainId: number | null;
    slug: string;
    name: string;
  };
  availability: {
    status: string;
    categories: AvailabilityCategory[];
    predictions: HourlyPrediction[];
    weekly: Record<string, WeekdayCategorySeries[]>;
  };
  theme: ThemeTokens;
  /** The premise's opening hours from its widget settings, already validated server-side. */
  hours: HourRange | null;
  /** The premise's max-width setting, already validated server-side. Null falls back to the
   *  widget's built-in 420px default. */
  maxWidth: string | null;
  /** The premise's text size from the widget admin, already validated server-side. Null
   *  falls back to medium, the widget's built-in sizing. */
  fontSize: FontSize | null;
  aiptEndpoint: string | null;
  /** Whether the "email me the plan" form should render within AI-PT. Always false when
   *  `aiptEndpoint` is null — a sub-feature can't be on when its parent feature is off. */
  aiptEmailEnabled: boolean;
}

interface PollPayload {
  status: string;
  categories: AvailabilityCategory[];
  predictions: HourlyPrediction[];
}

interface ChainAggregateRow {
  slug: string;
  name: string;
  status: string;
  total: { objectCount: number; estimatedCapacity: number | null } | null;
}

const POLL_INTERVAL_MS = 60_000;
const STYLE_ELEMENT_ID = "ii-gp-widget-styles";
const FONT_STYLE_ELEMENT_ID = "ii-gp-widget-font";
// ISO weekday numbering (1=Monday..7=Sunday), matching the API's own `dow` field.
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

function injectStylesOnce(): void {
  if (document.getElementById(STYLE_ELEMENT_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ELEMENT_ID;
  style.textContent = WIDGET_CSS;
  document.head.appendChild(style);
}

/** Separate from the main stylesheet so that a page whose first widget inherits the host
 *  font (and so never asks for the download) can still get Inter for a later instance. */
function injectFontFaceOnce(scriptUrl: URL): void {
  if (document.getElementById(FONT_STYLE_ELEMENT_ID)) return;
  const style = document.createElement("style");
  style.id = FONT_STYLE_ELEMENT_ID;
  style.textContent = fontFaceCss(new URL("fonts/", scriptUrl));
  document.head.appendChild(style);
}

function applyThemeTokens(root: HTMLElement, theme: ThemeTokens): void {
  const sizes = THEME_SIZES[theme.thickness];
  for (const [key, value] of Object.entries(theme)) {
    if (key === "thickness") continue;
    root.style.setProperty(`--gp-${key.replace(/_/g, "-")}`, String(value));
  }
  root.style.setProperty("--gp-border-width", `${sizes.borderWidth}px`);
  root.style.setProperty("--gp-border-radius", `${sizes.borderRadius}px`);
  root.style.setProperty("--gp-font-weight", String(sizes.fontWeight));
  root.style.setProperty("--gp-font-weight-bold", String(sizes.fontWeightBold));
  root.style.setProperty("--gp-text-transform", sizes.textTransform);
  root.style.setProperty("--gp-rt-total-padding", sizes.rtTotalPadding);
  root.style.setProperty("--gp-logo-text", logoTextColor(theme.realtime_bg));
}

/** Speech-bubble help text for one control, drawn by CSS from these attributes while the
 *  widget is in help mode — attributes survive the innerHTML re-renders the panels go through. */
function hintAttrs(
  text: string,
  at: "below-start" | "inside-top" | "center" | "below-center",
): string {
  return ` data-hint="${escapeHtml(text)}" data-hint-at="${at}"`;
}

type ForecastBasis = "history" | "capacity";

/** Plot hint plus what its "max" label stands for, which depends on where the scale came from. */
function plotHint(
  locale: Locale,
  key: string,
  basis: ForecastBasis | null,
): string {
  const max =
    basis === "history"
      ? t(locale, "maxIsPeak")
      : basis === "capacity"
        ? t(locale, "maxIsCapacity")
        : "";
  return max ? `${t(locale, key)}. ${max}` : t(locale, key);
}

/**
 * The shape of the Now tab before its numbers exist: the hero bar, four category boxes and the
 * curve, drawn as blank blocks at the sizes the real ones will take. A widget embedded in
 * someone else's page cannot afford to change height once it has data, and a line of text
 * saying "Loading" both jumps and has to be translated to say nothing. Four boxes because that
 * is the common case; if the premise has a different number the grid simply reflows once, which
 * is a smaller movement than going from one line of text to a full panel.
 */
function skeletonHtml(locale: Locale): string {
  const box = '<div class="ii-gp-widget__skeleton-box"></div>';
  return `
<div class="ii-gp-widget__skeleton" role="status" aria-busy="true" aria-label="${escapeHtml(t(locale, "loading"))}">
  <div class="ii-gp-widget__skeleton-hero">
    <span class="ii-gp-widget__skeleton-bar ii-gp-widget__skeleton-bar--label"></span>
    <span class="ii-gp-widget__skeleton-bar ii-gp-widget__skeleton-bar--count"></span>
  </div>
  <div class="ii-gp-widget__skeleton-boxes">${box.repeat(4)}</div>
  <span class="ii-gp-widget__skeleton-bar ii-gp-widget__skeleton-bar--plot-title"></span>
  <div class="ii-gp-widget__skeleton-curve"></div>
</div>`;
}

/**
 * The feedback block is mounted from the payload as well, because it is keyed by premise id, so
 * during a load its half of the foot stood empty and the widget grew the moment it arrived —
 * exactly the jump the panel skeleton exists to prevent. Title, five stars, question, at the
 * sizes the real ones take.
 */
function feedbackSkeletonHtml(locale: Locale): string {
  const star = '<span class="ii-gp-widget__skeleton-star"></span>';
  return `
<div class="ii-gp-widget__skeleton ii-gp-widget__skeleton--feedback" role="status" aria-busy="true" aria-label="${escapeHtml(t(locale, "loading"))}">
  <span class="ii-gp-widget__skeleton-bar ii-gp-widget__skeleton-bar--title"></span>
  <div class="ii-gp-widget__skeleton-stars">${star.repeat(5)}</div>
  <span class="ii-gp-widget__skeleton-bar ii-gp-widget__skeleton-bar--question"></span>
</div>`;
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        char
      ]!,
  );
}

/** Label on the left, the count on the right at hero size — the current number is what a
 *  visitor opened the widget for, so it gets the size; the label names it. */
/**
 * The current hour as the gym counts it: the first predicted point is one hour ahead and is
 * stamped in the gym's timezone, which the visitor's own clock need not match. Falls back to
 * the local clock when there are no predictions to read it from.
 */
function currentHourOf(predictions: HourlyPrediction[]): number {
  const next = predictions
    .filter((p) => p.categoryKey === "total")
    .sort((a, b) => a.offsetHours - b.offsetHours)[0];
  return next ? (next.hour + 23) % 24 : new Date().getHours();
}

function isClosedAt(hour: number, hours: HourRange | null): boolean {
  return hours !== null && (hour < hours.start || hour >= hours.end);
}

/** Shared by the Now tab and the compact total pill, so the two never disagree about
 *  whether the gym reads as closed. */
function closedUntilOf(
  predictions: HourlyPrediction[],
  hours: HourRange | null,
): string | null {
  return isClosedAt(currentHourOf(predictions), hours)
    ? `${String(hours!.start).padStart(2, "0")}:00`
    : null;
}

function renderTotalPill(
  categories: AvailabilityCategory[],
  closedUntil: string | null,
  locale: Locale,
): string {
  const total = categories.find((c) => c.key === "total");
  if (!total) return "";
  // Outside opening hours the live count is the one thing that means nothing — everything
  // else on the widget (forecast, week, plan) still does — so only it is replaced.
  if (closedUntil) {
    return `
<div class="ii-gp-widget__total ii-gp-widget__total--closed">
  <span class="ii-gp-widget__closed-label">${escapeHtml(t(locale, "closedLabel"))}</span>
  <span class="ii-gp-widget__closed-time">${escapeHtml(closedUntil)}</span>
</div>`;
  }
  const capacity = total.estimatedCapacity
    ? `<span class="ii-gp-widget__total-capacity">/ ${total.estimatedCapacity}</span>`
    : "";
  return `
<div class="ii-gp-widget__total">
  <span class="ii-gp-widget__total-label"><span class="ii-gp-widget__live-dot"></span>${escapeHtml(total.label)}</span>
  <span class="ii-gp-widget__total-count">${total.objectCount}${capacity}</span>
</div>`;
}

function renderBoxes(
  categories: AvailabilityCategory[],
  locale: Locale,
): string {
  const boxes = categories.filter((c) => c.key !== "total");
  // The count drives the column split (see the --nN rules): 4 boxes become 2×2 rather than
  // 3 + a stretched orphan.
  return `<div class="ii-gp-widget__boxes ii-gp-widget__boxes--n${boxes.length}"${hintAttrs(t(locale, "hintNow"), "center")}>${boxes
    .map(
      (category) => `
    <div class="ii-gp-widget__box">
      <div class="ii-gp-widget__box-count">${category.objectCount}${category.estimatedCapacity ? `<span class="ii-gp-widget__box-capacity">/ ${category.estimatedCapacity}</span>` : ""}</div>
      <div class="ii-gp-widget__box-label">${escapeHtml(category.label)}</div>
    </div>`,
    )
    .join("")}</div>`;
}

/**
 * One curve on the shared scale (see computeForecastScale): zero at the bottom, the premise's
 * busiest hour at the top ("max · N"), a smooth area between, coloured by level — green empty,
 * amber mid-way, red full — with the gradient pinned to the axis so the colour under any point
 * reads as "how full".
 * Inline SVG with a stretchable viewBox (preserveAspectRatio="none") so it fills any width; all
 * text is HTML around it so it never distorts. With no scale the curve is drawn to its own peak
 * and the max/0 labels are left off. The marker line is always present (hidden when unused) so a
 * caller can move it without re-rendering.
 */
function buildCurve(
  values: number[],
  forecastScale: number | null,
  locale: Locale,
  variant: "" | "week",
  markerIndex: number | null,
): string {
  const n = Math.max(1, values.length);
  const hasScale = forecastScale != null && forecastScale > 0;
  const scale = hasScale ? forecastScale : Math.max(1, ...values);
  const xy = values.map((v, i) => ({
    x: ((i + 0.5) / n) * 100,
    y: 100 - Math.min(100, Math.max(0, (v / scale) * 100)),
  }));
  const line = [
    { x: 0, y: xy[0]?.y ?? 100 },
    ...xy,
    { x: 100, y: xy[n - 1]?.y ?? 100 },
  ];
  const clampY = (v: number) => Math.min(100, Math.max(0, v));
  let d = `M ${line[0].x} ${line[0].y}`;
  for (let i = 0; i < line.length - 1; i++) {
    const p0 = line[i - 1] ?? line[i],
      p1 = line[i],
      p2 = line[i + 1],
      p3 = line[i + 2] ?? p2;
    const c1x = p1.x + (p2.x - p0.x) / 6,
      c1y = clampY(p1.y + (p2.y - p0.y) / 6);
    const c2x = p2.x - (p3.x - p1.x) / 6,
      c2y = clampY(p2.y - (p3.y - p1.y) / 6);
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
  }
  const area = `${d} L 100 100 L 0 100 Z`;
  const gradientId = `ii-gp-forecast-${Math.random().toString(36).slice(2, 8)}`;
  const xm = markerIndex == null ? 0 : ((markerIndex + 0.5) / n) * 100;
  const axis = hasScale
    ? `<span class="ii-gp-widget__forecast-axis ii-gp-widget__forecast-axis--top">${scale} ${escapeHtml(t(locale, "personsShort"))}</span>
      <span class="ii-gp-widget__forecast-axis ii-gp-widget__forecast-axis--bottom">0</span>`
    : "";
  return `
    <div class="ii-gp-widget__forecast-canvas${variant ? ` ii-gp-widget__forecast-canvas--${variant}` : ""}">
      <svg class="ii-gp-widget__forecast-svg" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id="${gradientId}" gradientUnits="userSpaceOnUse" x1="0" y1="100" x2="0" y2="0">
            <stop offset="0" style="stop-color: var(--gp-forecast-low)" />
            <stop offset="0.5" style="stop-color: var(--gp-forecast-mid)" />
            <stop offset="1" style="stop-color: var(--gp-forecast-high)" />
          </linearGradient>
        </defs>
        <line class="ii-gp-widget__forecast-grid" x1="0" y1="0" x2="100" y2="0" vector-effect="non-scaling-stroke" />
        <line class="ii-gp-widget__forecast-grid ii-gp-widget__forecast-grid--mid" x1="0" y1="50" x2="100" y2="50" vector-effect="non-scaling-stroke" />
        <line class="ii-gp-widget__forecast-grid" x1="0" y1="100" x2="100" y2="100" vector-effect="non-scaling-stroke" />
        <path class="ii-gp-widget__forecast-area" d="${area}" fill="url(#${gradientId})" />
        <path class="ii-gp-widget__forecast-line" d="${d}" stroke="url(#${gradientId})" vector-effect="non-scaling-stroke" />
        <line class="ii-gp-widget__forecast-marker" x1="${xm}" y1="0" x2="${xm}" y2="100" vector-effect="non-scaling-stroke"${markerIndex == null ? ' style="display: none"' : ""} />
      </svg>
      ${axis}
    </div>`;
}

/** The next hours, on the shared curve. Head-counts are deliberately not shown — the question
 *  this panel answers is "how full will it be"; the absolute count is the headline above. */
function renderPredictions(
  predictions: HourlyPrediction[],
  forecastScale: number | null,
  forecastBasis: ForecastBasis | null,
  hours: HourRange | null,
  locale: Locale,
): string {
  const points = predictions
    .filter((p) => p.categoryKey === "total")
    .filter((p) => !hours || (p.hour >= hours.start && p.hour < hours.end))
    .sort((a, b) => a.offsetHours - b.offsetHours);
  if (points.length === 0) return "";
  const hourLabels = points
    .map(
      (p) =>
        `<span${p.outsideTypicalHours ? ' class="ii-gp-widget__forecast-hour--outside"' : ""}>${String(p.hour).padStart(2, "0")}</span>`,
    )
    .join("");
  return `
<div class="ii-gp-widget__predictions">
  <p class="ii-gp-widget__predictions-title">${escapeHtml(t(locale, "availabilityForNextHours"))}</p>
  <div class="ii-gp-widget__forecast"${hintAttrs(plotHint(locale, "hintForecast", forecastBasis), "inside-top")}>
    ${buildCurve(
      points.map((p) => p.predictedCount),
      forecastScale,
      locale,
      "",
      null,
    )}
    <div class="ii-gp-widget__forecast-hours">${hourLabels}</div>
  </div>
</div>`;
}

/** Sum of every category per hour of one weekday, plus the per-hour split behind it. */
function weekdayTotals(series: WeekdayCategorySeries[]): {
  totals: number[];
  byHour: { key: string; count: number }[][];
} {
  const totals = Array.from({ length: 24 }, () => 0);
  const byHour = Array.from(
    { length: 24 },
    () => [] as { key: string; count: number }[],
  );
  for (const s of series) {
    for (const h of s.hours) {
      if (h.hour < 0 || h.hour > 23) continue;
      totals[h.hour] += h.objectCount;
      byHour[h.hour].push({ key: s.categoryKey, count: h.objectCount });
    }
  }
  return { totals, byHour };
}

/**
 * Week tab body: one weekday as the same curve the forecast uses (total visitors per hour on
 * the shared scale), with the hours row acting as a scrubber — tap or hover an hour and the
 * list below shows how that hour's total splits across categories. Today opens on the current
 * hour, other days on their busiest one.
 */
function renderWeekCurve(
  container: HTMLElement,
  series: WeekdayCategorySeries[],
  categoryLabels: Record<string, string>,
  forecastScale: number | null,
  forecastBasis: ForecastBasis | null,
  hours: HourRange | null,
  locale: Locale,
  isToday: boolean,
): void {
  const { totals, byHour } = weekdayTotals(series);
  // The shown slice of the day. Everything drawn is indexed within it; `totals` and `byHour`
  // stay full-day arrays, so an hour is still its own index.
  const first = hours?.start ?? 0;
  const last = (hours?.end ?? 24) - 1;
  const shownCount = last - first + 1;
  const clamp = (hour: number) => Math.min(last, Math.max(first, hour));
  const shown = totals.slice(first, last + 1);
  const peakHour = first + shown.indexOf(Math.max(...shown));
  const initial = clamp(isToday ? new Date().getHours() : peakHour);
  const hoursRow = shown
    .map((_, i) => first + i)
    .map(
      (h) =>
        `<button type="button" class="ii-gp-widget__forecast-hour" data-hour="${h}" aria-label="${String(h).padStart(2, "0")}:00">${h % 3 === 0 ? String(h).padStart(2, "0") : ""}</button>`,
    )
    .join("");
  container.innerHTML = `
    <div class="ii-gp-widget__forecast"${hintAttrs(plotHint(locale, "hintWeek", forecastBasis), "center")}>
      ${buildCurve(shown, forecastScale, locale, "week", initial - first)}
      <div class="ii-gp-widget__forecast-hours ii-gp-widget__forecast-hours--scrub">${hoursRow}</div>
    </div>
    <div class="ii-gp-widget__breakdown"></div>`;

  const marker = container.querySelector<SVGLineElement>(
    ".ii-gp-widget__forecast-marker",
  )!;
  const hourButtons = container.querySelectorAll<HTMLButtonElement>(
    ".ii-gp-widget__forecast-hour",
  );
  const breakdown = container.querySelector<HTMLDivElement>(
    ".ii-gp-widget__breakdown",
  )!;
  const canvas = container.querySelector<HTMLDivElement>(
    ".ii-gp-widget__forecast-canvas",
  )!;
  const peopleWord = t(locale, "people").toLowerCase();

  const select = (hour: number) => {
    const x = ((hour - first + 0.5) / shownCount) * 100;
    marker.setAttribute("x1", String(x));
    marker.setAttribute("x2", String(x));
    // display, not visibility: an explicit visibility:visible would override the inactive
    // panel's visibility:hidden and show the marker through the other tab.
    marker.style.display = "";
    hourButtons.forEach((b) =>
      b.classList.toggle(
        "ii-gp-widget__forecast-hour--selected",
        Number(b.dataset.hour) === hour,
      ),
    );
    const rows = [...byHour[hour]]
      .sort((a, b) => b.count - a.count)
      .map(
        (r) =>
          `<div class="ii-gp-widget__breakdown-row"><span>${escapeHtml(categoryLabels[r.key] ?? r.key)}</span><span>${r.count}</span></div>`,
      )
      .join("");
    breakdown.innerHTML = `
      <div class="ii-gp-widget__breakdown-summary">
        <div class="ii-gp-widget__breakdown-hour">${String(hour).padStart(2, "0")}:00</div>
        <div class="ii-gp-widget__breakdown-total"><span class="ii-gp-widget__breakdown-count">${totals[hour]}</span> <span class="ii-gp-widget__breakdown-unit">${escapeHtml(peopleWord)}</span></div>
      </div>
      <div class="ii-gp-widget__breakdown-rows">${rows}</div>`;
  };

  hourButtons.forEach((button) => {
    const hour = Number(button.dataset.hour);
    button.addEventListener("click", () => select(hour));
    // Hover scrubs on a mouse; on touch the tap (click) is the whole gesture.
    button.addEventListener("pointerenter", (event) => {
      if (event.pointerType === "mouse") select(hour);
    });
  });
  // The plot itself scrubs too, not just the hour labels: the x position maps straight onto
  // the equal slots the curve is drawn in. Moving a mouse across it scrubs; a tap selects.
  const hourAtX = (clientX: number) => {
    const rect = canvas.getBoundingClientRect();
    return clamp(
      first + Math.floor(((clientX - rect.left) / rect.width) * shownCount),
    );
  };
  canvas.addEventListener("pointermove", (event) => {
    if (event.pointerType === "mouse") select(hourAtX(event.clientX));
  });
  canvas.addEventListener("pointerdown", (event) =>
    select(hourAtX(event.clientX)),
  );
  select(initial);
}

function renderNowTab(
  payload: {
    categories: AvailabilityCategory[];
    predictions: HourlyPrediction[];
  },
  forecastScale: number | null,
  forecastBasis: ForecastBasis | null,
  hours: HourRange | null,
  locale: Locale,
): string {
  const closedUntil = closedUntilOf(payload.predictions, hours);
  return `
<div class="ii-gp-widget__now-head${closedUntil ? " ii-gp-widget__now-head--closed" : ""}">
${renderTotalPill(payload.categories, closedUntil, locale)}
${closedUntil ? "" : renderBoxes(payload.categories, locale)}
</div>
${renderPredictions(payload.predictions, forecastScale, forecastBasis, hours, locale)}`;
}

async function fetchJson<T>(url: URL): Promise<T> {
  const response = await fetch(url);
  if (!response.ok)
    throw new Error(
      `Request to ${url.pathname} failed: HTTP ${response.status}`,
    );
  return response.json() as Promise<T>;
}

async function renderChainPanel(
  container: HTMLElement,
  apiOrigin: string,
  scriptUrl: URL,
  chainId: number,
  slug: string,
  locale: Locale,
): Promise<void> {
  try {
    const url = new URL(`/chains/${chainId}/aggregate`, apiOrigin);
    url.searchParams.set("lang", locale);
    const data = await fetchJson<{ premises: ChainAggregateRow[] }>(url);
    const others = data.premises.filter((p) => p.slug !== slug);
    if (others.length === 0) return;

    const rows = others
      .map((row) => {
        const countText = row.total
          ? `${row.total.objectCount}${row.total.estimatedCapacity ? ` / ${row.total.estimatedCapacity}` : ""}`
          : row.status;
        // No external "which URL is this premise embedded on" field exists in our data model
        // (see VIBE_CODE_PLAN.md's field list) — this links to our own render endpoint for
        // that slug as the closest available stand-in for "that premise's own embed page"
        // until a dedicated standalone per-premise page exists.
        const href = new URL(
          `/premises/${row.slug}/render`,
          scriptUrl,
        ).toString();
        return `<a class="ii-gp-widget__chain-row" href="${escapeHtml(href)}" data-slug="${escapeHtml(row.slug)}" target="_blank" rel="noopener">
          <span>${escapeHtml(row.name)}</span>
          <span class="ii-gp-widget__chain-count">${escapeHtml(countText)}</span>
        </a>`;
      })
      .join("");

    container.innerHTML = `<p class="ii-gp-widget__chain-title">${escapeHtml(t(locale, "otherLocations"))}</p>${rows}`;
  } catch (error) {
    console.error("[ii-gp-widget] chain panel failed to load", error);
  }
}

function init(script: HTMLScriptElement): void {
  const slugParam = script.dataset.premise;
  if (!slugParam) {
    console.error("[ii-gp-widget] missing required data-premise attribute");
    return;
  }
  // Re-bound to a real `string`, not just a narrowed union — narrowing from the guard above
  // doesn't reach into closures declared further down.
  const slug: string = slugParam;
  const locale: Locale = (["fi", "en", "sv"] as const).includes(
    script.dataset.lang as Locale,
  )
    ? (script.dataset.lang as Locale)
    : "fi";
  const themeOverride = script.dataset.theme;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  const strings = STRINGS[locale];
  // Derived from the script's own src, not hardcoded — the widget works from whatever
  // domain it's actually served from (fixes the old hardcoded-domain bug).
  const scriptUrl = new URL(script.src);
  const apiOrigin = scriptUrl.origin;
  const fontMode: FontMode = parseFont(script.dataset.font);
  const showAipt = parseAipt(script.dataset.aipt);
  // data-type="onlytotal": render just the live total, with a "see more" link that opens the
  // full widget (built from the same markup and data) in a page-level modal. See the
  // `compact` usages below and the .ii-gp-widget-modal-backdrop rules in styles.ts.
  const compact = parseDisplayMode(script.dataset.type) === "onlytotal";
  const privacyUrl = parsePrivacyUrl(
    script.dataset.privacyUrl,
    document.baseURI,
  );
  // The embed attribute overrides the premise's stored opening hours, which arrive with the
  // render payload below.
  const hoursOverride = parseHours(script.dataset.hours);
  let hours: HourRange | null = hoursOverride;

  injectStylesOnce();
  if (fontMode === "inter") injectFontFaceOnce(scriptUrl);
  const track = createTracker({ apiOrigin, slug, locale });

  // The tabs/panels/foot/chain/footer markup — identical whether it ends up inline (the
  // regular embed) or inside the compact view's "see more" modal (built further down), so
  // there is exactly one template for what a "full" widget looks like.
  //
  // The two tab panels share one grid cell, so the widget is as tall as the taller of them on
  // both tabs and never jumps when switching. AI-PT opens as an overlay absolutely positioned
  // over `.ii-gp-widget__main` (tabs + panels + foot), covering exactly that footprint rather
  // than growing it — the form, loader and answer all vary in height, but the widget itself
  // never does. `.ii-gp-widget__main-content` holds the content the overlay covers, kept as a
  // sibling rather than a parent of the overlay so it can be marked `inert` while the overlay
  // is open without taking the overlay down with it.
  const fullWidgetHtml = (): string => `
<div class="ii-gp-widget__main">
<div class="ii-gp-widget__main-content">
<div class="ii-gp-widget__tabs"${hintAttrs(t(locale, "hintTabs"), "below-start")}>
  <button class="ii-gp-widget__tab ii-gp-widget__tab--active" data-tab="now">${escapeHtml(t(locale, "now"))}</button>
  <button class="ii-gp-widget__tab" data-tab="week">${escapeHtml(t(locale, "week"))}</button>
</div>
<div class="ii-gp-widget__panels">
  <div class="ii-gp-widget__panel ii-gp-widget__panel--active" data-panel="now"></div>
  <div class="ii-gp-widget__panel" data-panel="week"></div>
</div>
<div class="ii-gp-widget__foot">
<div class="ii-gp-widget__aipt"${showAipt ? "" : " hidden"}>
  <div class="ii-gp-widget__aipt-cta"${hintAttrs(t(locale, "hintAipt"), "below-center")}>
    <button type="button" class="ii-gp-widget__aipt-open"><span class="ii-gp-widget__aipt-open-long">${escapeHtml(t(locale, "aipt"))}</span><span class="ii-gp-widget__aipt-open-short">${escapeHtml(t(locale, "aiptShort"))}</span></button>
    <div class="ii-gp-widget__aipt-figures" aria-hidden="true">
      <img class="ii-gp-widget__aipt-figure ii-gp-widget__aipt-figure--back" src="${escapeHtml(new URL("img/pt-female.png", scriptUrl).toString())}" alt="" />
      <img class="ii-gp-widget__aipt-figure ii-gp-widget__aipt-figure--front" src="${escapeHtml(new URL("img/pt-male.png", scriptUrl).toString())}" alt="" />
    </div>
  </div>
</div>
<div class="ii-gp-widget__feedback"></div>
</div>
</div>
<div class="ii-gp-widget__aipt-overlay" hidden>
  <div class="ii-gp-widget__aipt-head">
    <span>${escapeHtml(t(locale, "aipt"))}</span>
    <button type="button" class="ii-gp-widget__aipt-close" aria-label="${escapeHtml(t(locale, "close"))}">×</button>
  </div>
  <div class="ii-gp-widget__aipt-body"></div>
</div>
</div>
<div class="ii-gp-widget__chain"></div>
<div class="ii-gp-widget__footer"><button type="button" class="ii-gp-widget__help" aria-label="${escapeHtml(t(locale, "help"))}" aria-pressed="false">i</button>${privacyUrl ? `<a class="ii-gp-widget__privacy" href="${escapeHtml(privacyUrl)}" target="_blank" rel="noopener">${escapeHtml(t(locale, "privacy"))}</a>` : ""}<a class="ii-gp-widget__footer-link" href="https://www.gymplus.fi" target="_blank" rel="noopener">${gymplusLogoSvg(escapeHtml(strings.poweredBy))}</a></div>`;

  const widgetClassName = [
    "ii-gp-widget",
    fontMode === "inherit" ? "ii-gp-widget--inherit-font" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const root = document.createElement("div");
  // data-type="onlytotal": `root` carries only the compact bar (live total + "see more") —
  // the full widget is a second instance of the same markup, built below and mounted into
  // document.body as a real page-level modal, not nested inside this small host. Nesting it
  // here does not work: .ii-gp-widget-host's `container-type: inline-size` (below) makes it a
  // containing block for `position: fixed` descendants too, so a "modal" inside it would be
  // clipped to the compact bar's own small box instead of covering the page.
  root.className = compact
    ? `${widgetClassName} ii-gp-widget--compact`
    : widgetClassName;
  root.innerHTML = compact
    ? `
<div class="ii-gp-widget__compact">
  <div class="ii-gp-widget__compact-total">
    <div class="ii-gp-widget__skeleton ii-gp-widget__skeleton-hero" role="status" aria-busy="true" aria-label="${escapeHtml(t(locale, "loading"))}">
      <span class="ii-gp-widget__skeleton-bar ii-gp-widget__skeleton-bar--label"></span>
      <span class="ii-gp-widget__skeleton-bar ii-gp-widget__skeleton-bar--count"></span>
    </div>
  </div>
  <button type="button" class="ii-gp-widget__compact-more">${escapeHtml(t(locale, "seeMore"))}</button>
</div>`
    : fullWidgetHtml();

  // The widget goes inside a host element that owns the width cap and acts as the query
  // container for the sizing tiers, so the widget adapts to the box the embedder gives it.
  const host = document.createElement("div");
  host.className = "ii-gp-widget-host";
  // The embed attribute overrides the premise's stored max-width, which arrives with the
  // render payload below — same precedence as `hours` above.
  const maxWidthOverride = parseMaxWidth(script.dataset.maxWidth);
  if (maxWidthOverride) host.style.setProperty("--gp-max-width", maxWidthOverride);
  // Same precedence for the text size. An embed override applies right away (below, once the
  // modal card exists too); the stored setting only once the render payload is in.
  const fontSizeOverride = parseFontSize(script.dataset.fontSize);
  host.appendChild(root);
  script.insertAdjacentElement("afterend", host);

  // The compact view's "see more" modal: a full second `.ii-gp-widget-host > .ii-gp-widget`
  // pair (so it gets its own container-query scope, sized off its own width like any embed),
  // wrapped in a fixed, viewport-covering backdrop and appended straight to <body> so nothing
  // about the compact bar's own box — width, overflow, or the containment above — constrains
  // it. Built and mounted up front rather than on first "see more" click: `load()` below fills
  // both instances from the same fetch, so there is nothing left to mount lazily.
  let modalBackdrop: HTMLDivElement | null = null;
  let modalWidgetHost: HTMLDivElement | null = null;
  let modalRoot: HTMLDivElement | null = null;
  let modalClose: HTMLButtonElement | null = null;
  if (compact) {
    modalBackdrop = document.createElement("div");
    modalBackdrop.className = "ii-gp-widget-modal-backdrop";
    modalBackdrop.hidden = true;
    modalWidgetHost = document.createElement("div");
    modalWidgetHost.className = "ii-gp-widget-host";
    if (maxWidthOverride) modalWidgetHost.style.setProperty("--gp-max-width", maxWidthOverride);
    modalRoot = document.createElement("div");
    modalRoot.className = widgetClassName;
    modalRoot.innerHTML = fullWidgetHtml();
    modalClose = document.createElement("button");
    modalClose.type = "button";
    modalClose.className = "ii-gp-widget__full-close";
    modalClose.setAttribute("aria-label", t(locale, "close"));
    modalClose.textContent = "×";
    modalRoot.prepend(modalClose);
    modalWidgetHost.appendChild(modalRoot);
    modalBackdrop.appendChild(modalWidgetHost);
    document.body.appendChild(modalBackdrop);
  }
  // One modifier class per card sets --gp-font-scale, which every --gp-fs-* size in styles.ts
  // is multiplied by. Medium is the built-in sizing, so it carries no class.
  const applyFontSize = (size: FontSize | null) => {
    for (const card of [root, modalRoot]) {
      if (!card) continue;
      card.classList.toggle("ii-gp-widget--font-small", size === "small");
      card.classList.toggle("ii-gp-widget--font-large", size === "large");
    }
  };
  applyFontSize(fontSizeOverride);
  // Whichever element actually holds the tabs/panels/foot markup: `root` itself outside
  // compact mode, or the detached modal's card when compact.
  const widgetRoot = modalRoot ?? root;

  const tabs = widgetRoot.querySelectorAll<HTMLButtonElement>(".ii-gp-widget__tab");
  const panels = widgetRoot.querySelectorAll<HTMLDivElement>(".ii-gp-widget__panel");
  const nowPanel = widgetRoot.querySelector<HTMLDivElement>('[data-panel="now"]')!;
  const weekPanel = widgetRoot.querySelector<HTMLDivElement>('[data-panel="week"]')!;
  const aiptOpen = widgetRoot.querySelector<HTMLButtonElement>(
    ".ii-gp-widget__aipt-open",
  )!;
  const aiptCta = widgetRoot.querySelector<HTMLDivElement>(
    ".ii-gp-widget__aipt-cta",
  )!;
  const helpButton = widgetRoot.querySelector<HTMLButtonElement>(
    ".ii-gp-widget__help",
  )!;
  const setHelp = (on: boolean) => {
    widgetRoot.classList.toggle("ii-gp-widget--help", on);
    helpButton.setAttribute("aria-pressed", String(on));
  };
  helpButton.addEventListener("click", () => {
    const on = !widgetRoot.classList.contains("ii-gp-widget--help");
    setHelp(on);
    if (on) track("help");
  });
  widgetRoot.addEventListener("keydown", (event) => {
    if (event.key === "Escape") setHelp(false);
  });
  const aiptOverlay = widgetRoot.querySelector<HTMLDivElement>(
    ".ii-gp-widget__aipt-overlay",
  )!;
  const aiptClose = widgetRoot.querySelector<HTMLButtonElement>(
    ".ii-gp-widget__aipt-close",
  )!;
  const aiptBody = widgetRoot.querySelector<HTMLDivElement>(
    ".ii-gp-widget__aipt-body",
  )!;
  const mainContent = widgetRoot.querySelector<HTMLDivElement>(
    ".ii-gp-widget__main-content",
  )!;
  const chainPanel = widgetRoot.querySelector<HTMLDivElement>(
    ".ii-gp-widget__chain",
  )!;
  // Delegated, since the chain rows are rendered later (and only for chain members).
  chainPanel.addEventListener("click", (event) => {
    const row = (event.target as Element).closest<HTMLAnchorElement>(
      ".ii-gp-widget__chain-row",
    );
    if (row?.dataset.slug) track("chain_click", row.dataset.slug);
  });
  widgetRoot
    .querySelector(".ii-gp-widget__footer-link")
    ?.addEventListener("click", () => track("link", "gymplus"));
  widgetRoot
    .querySelector(".ii-gp-widget__privacy")
    ?.addEventListener("click", () => track("link", "privacy"));
  const feedbackPanel = widgetRoot.querySelector<HTMLDivElement>(
    ".ii-gp-widget__feedback",
  )!;
  const compactTotal = root.querySelector<HTMLDivElement>(
    ".ii-gp-widget__compact-total",
  )!;

  if (compact && modalBackdrop && modalClose) {
    const backdrop = modalBackdrop;
    const close = modalClose;
    const compactMore = root.querySelector<HTMLButtonElement>(
      ".ii-gp-widget__compact-more",
    )!;
    const setModalOpen = (open: boolean) => {
      backdrop.hidden = !open;
      if (open) close.focus();
      else compactMore.focus();
    };
    compactMore.addEventListener("click", () => {
      setModalOpen(true);
      track("seemore");
    });
    close.addEventListener("click", () => setModalOpen(false));
    // Backdrop click closes too, but not a click that started inside the card and was
    // released over the backdrop (e.g. a text selection drag) — only a click that both
    // started and ended on the backdrop itself counts.
    backdrop.addEventListener("mousedown", (event) => {
      if (event.target !== backdrop) return;
      backdrop.addEventListener(
        "mouseup",
        (up) => {
          if (up.target === backdrop) setModalOpen(false);
        },
        { once: true },
      );
    });
    widgetRoot.addEventListener("keydown", (event) => {
      // With AI-PT open, Escape closes just that overlay (its own handler, below) — not the
      // whole modal along with it.
      if (event.key === "Escape" && !backdrop.hidden && aiptOverlay.hidden) setModalOpen(false);
    });
  }

  let latestData: RenderPayload | null = null;
  // Fixed from the render payload's week of history; the 60s poll carries no history, so it
  // reuses this rather than re-deriving a (necessarily worse) scale from six points.
  let forecastScale: number | null = null;
  let forecastBasis: ForecastBasis | null = null;
  let activeTab: "now" | "week" = "now";
  // ISO weekday (1 = Monday … 7 = Sunday), starting on today.
  const todayIso = ((new Date().getDay() + 6) % 7) + 1;
  let activeWeekday = todayIso;
  let aiptMounted = false;
  // A retry after a failed first load must not count as a second view.
  let viewTracked = false;

  /** The compact view's whole content — same pill markup as the Now tab's hero, kept in sync
   *  with it on load and on every poll. No-op when data-type is not "onlytotal". */
  function updateCompactTotal(
    categories: AvailabilityCategory[],
    predictions: HourlyPrediction[],
  ): void {
    if (!compact) return;
    compactTotal.innerHTML = renderTotalPill(
      categories,
      closedUntilOf(predictions, hours),
      locale,
    );
  }

  function renderWeekPanel(): void {
    if (!latestData) return;
    const data = latestData;
    const categoryLabels = Object.fromEntries(
      data.availability.categories.map((c) => [c.key, c.label]),
    );
    const buttons = WEEKDAY_ORDER.map(
      (dayKey, index) =>
        `<button type="button" class="ii-gp-widget__weekday" data-weekday="${WEEKDAYS[index]}">${escapeHtml(t(locale, dayKey))}</button>`,
    ).join("");
    weekPanel.innerHTML = `<div class="ii-gp-widget__weekdays">${buttons}</div><div class="ii-gp-widget__week-body"></div>`;
    const body = weekPanel.querySelector<HTMLDivElement>(
      ".ii-gp-widget__week-body",
    )!;
    const weekdayButtons = weekPanel.querySelectorAll<HTMLButtonElement>(
      ".ii-gp-widget__weekday",
    );
    const show = () => {
      weekdayButtons.forEach((btn) =>
        btn.classList.toggle(
          "ii-gp-widget__weekday--active",
          Number(btn.dataset.weekday) === activeWeekday,
        ),
      );
      renderWeekCurve(
        body,
        data.availability.weekly[String(activeWeekday)] ?? [],
        categoryLabels,
        forecastScale,
        forecastBasis,
        hours,
        locale,
        activeWeekday === todayIso,
      );
    };
    weekdayButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const weekday = Number(button.dataset.weekday);
        if (weekday !== activeWeekday) track("weekday", String(weekday));
        activeWeekday = weekday;
        show();
      });
    });
    show();
  }

  function setActiveTab(tab: "now" | "week"): void {
    activeTab = tab;
    tabs.forEach((button) =>
      button.classList.toggle(
        "ii-gp-widget__tab--active",
        button.dataset.tab === tab,
      ),
    );
    panels.forEach((panel) =>
      panel.classList.toggle(
        "ii-gp-widget__panel--active",
        panel.dataset.panel === tab,
      ),
    );
    if (tab === "now" && latestData)
      nowPanel.innerHTML = renderNowTab(
        latestData.availability,
        forecastScale,
        forecastBasis,
        hours,
        locale,
      );
  }

  tabs.forEach((button) => {
    button.addEventListener("click", () => {
      const tab = button.dataset.tab as "now" | "week";
      if (tab !== activeTab) track("tab", tab);
      setActiveTab(tab);
    });
  });

  // AI-PT opens as an overlay above the tabs/panels/foot rather than in their flow, so the
  // widget's footprint stays exactly what it was — the overlay covers it instead of growing
  // it, and its own body scrolls internally once content (loader, answer) runs long. The
  // covered content is marked inert rather than hidden, so it keeps the height it already had
  // (nothing to reflow around) while also leaving the tab order and survey below alone: the
  // AI-PT flow ends in its own thumbs vote, so that survey shouldn't be reachable, let alone
  // asked twice, while the overlay is up. AI-PT's own state (form / loading / answer) persists
  // across open and close because it is mounted once.
  const setAiptOpen = (open: boolean) => {
    aiptOverlay.hidden = !open;
    mainContent.inert = open;
    widgetRoot.classList.toggle("ii-gp-widget--aipt-open", open);
    if (open) {
      aiptClose.focus();
    } else {
      aiptOpen.focus();
    }
    if (open && !aiptMounted && latestData) {
      aiptMounted = true;
      if (latestData.aiptEndpoint) {
        mountAiptTab(aiptBody, {
          slug,
          premiseId: latestData.premise.premiseId,
          locale,
          aiptEndpoint: latestData.aiptEndpoint,
          emailEnabled: latestData.aiptEmailEnabled,
          onSubmit: () => track("updatept"),
        });
      } else {
        aiptBody.innerHTML = `<div class="ii-gp-widget__error">${escapeHtml(t(locale, "aiptErrorUnavailable"))}</div>`;
      }
    }
  };
  aiptOpen.addEventListener("click", () => {
    setAiptOpen(true);
    track("aipt_open");
  });
  aiptClose.addEventListener("click", () => setAiptOpen(false));
  widgetRoot.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !aiptOverlay.hidden) setAiptOpen(false);
  });

  function renderUrl(): URL {
    const url = new URL(`/premises/${slug}/render`, apiOrigin);
    url.searchParams.set("lang", locale);
    if (themeOverride) url.searchParams.set("theme", themeOverride);
    return url;
  }

  function pollUrl(): URL {
    const url = new URL(`/premises/${slug}/poll`, apiOrigin);
    url.searchParams.set("lang", locale);
    return url;
  }

  // `root` (the compact bar's own total pill) and `widgetRoot` (the tabs/panels/foot, whether
  // that is `root` itself or the detached modal's card) both carry theme tokens as inline
  // custom properties, not by inheritance — the modal instance lives outside `root`'s subtree
  // once compact, precisely so its `position: fixed` isn't contained by `.ii-gp-widget-host`'s
  // container-type (see the comment above). Applied to the same element twice when not
  // compact, which is harmless.
  function applyTheme(theme: ThemeTokens): void {
    applyThemeTokens(root, theme);
    if (widgetRoot !== root) applyThemeTokens(widgetRoot, theme);
  }

  // The premise's own theme arrives with the render payload, so until then the widget wears
  // the CSS fallbacks, which are the light preset. That is fine when the request succeeds and
  // wrong when it does not: an embed asking for the dark preset drew a white error panel. When
  // a preset is named on the tag we can settle the colours before asking for anything.
  if (
    themeOverride &&
    (BUILT_IN_THEME_NAMES as readonly string[]).includes(themeOverride)
  ) {
    applyTheme(computeThemeTokens(undefined, themeOverride as BuiltInThemeName));
  }

  /**
   * Loading and failed are states of the whole widget, not of the Now panel. Everything except
   * that panel is wired from the render payload: the week curve is drawn from it, the plan
   * needs the premise's endpoint out of it, the feedback widget is mounted with its id. Left
   * alone they sat there looking operable while there was nothing behind them, so the two
   * states are marked on the root and the CSS decides what may be touched.
   */
  function setLoadState(state: "loading" | "failed" | "ready"): void {
    widgetRoot.classList.toggle("ii-gp-widget--loading", state === "loading");
    widgetRoot.classList.toggle("ii-gp-widget--failed", state === "failed");
  }

  function renderLoadError(): void {
    if (compact) {
      compactTotal.innerHTML = `<div class="ii-gp-widget__total ii-gp-widget__total--closed"><span class="ii-gp-widget__closed-label">${escapeHtml(t(locale, "loadError"))}</span></div>`;
    }
    // The week panel keeps whatever it was last given, and after a failed load that is a
    // skeleton with nothing coming. Hidden with the tabs, but still animating.
    weekPanel.innerHTML = "";
    feedbackPanel.innerHTML = "";
    nowPanel.innerHTML = `
      <div class="ii-gp-widget__error" role="status">
        <svg class="ii-gp-widget__error-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 3.8 2.6 20h18.8L12 3.8Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>
          <path d="M12 10v4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
          <circle cx="12" cy="17" r="1" fill="currentColor"/>
        </svg>
        <p class="ii-gp-widget__error-title">${escapeHtml(t(locale, "loadError"))}</p>
        <p class="ii-gp-widget__error-note">${escapeHtml(t(locale, "loadErrorNote"))}</p>
        <button type="button" class="ii-gp-widget__error-retry">${escapeHtml(t(locale, "retry"))}</button>
      </div>`;
    const retry = nowPanel.querySelector<HTMLButtonElement>(
      ".ii-gp-widget__error-retry",
    );
    retry?.addEventListener("click", () => load());
  }

  function load(): void {
    setLoadState("loading");
    // Both panels, because they share one grid cell: a visitor who switches tabs while the
    // request is in flight should find the same shape waiting, not an empty box.
    nowPanel.innerHTML = skeletonHtml(locale);
    weekPanel.innerHTML = skeletonHtml(locale);
    feedbackPanel.innerHTML = feedbackSkeletonHtml(locale);
    fetchJson<RenderPayload>(renderUrl())
      .then((data) => {
        setLoadState("ready");
        latestData = data;
        if (!viewTracked) {
          viewTracked = true;
          track("view", compact ? "onlytotal" : "full");
        }
        // The embed attribute (showAipt, resolved at mount time) and the premise's own
        // app_settings.gp_widget.features.aipt (resolved here, once the render payload is in)
        // both have to allow it — either one can turn the CTA off.
        aiptCta.hidden = !data.aiptEndpoint;
        hours = hoursOverride ?? data.hours ?? null;
        const maxWidth = maxWidthOverride ?? data.maxWidth;
        if (maxWidth) {
          host.style.setProperty("--gp-max-width", maxWidth);
          modalWidgetHost?.style.setProperty("--gp-max-width", maxWidth);
        }
        applyFontSize(fontSizeOverride ?? data.fontSize ?? null);
        const scale = computeForecastScale(
          data.availability.weekly,
          data.availability.categories.find((c) => c.key === "total")
            ?.estimatedCapacity,
          hours,
        );
        forecastScale = scale?.value ?? null;
        forecastBasis = scale?.basis ?? null;
        applyTheme(data.theme);
        setActiveTab("now");
        updateCompactTotal(
          data.availability.categories,
          data.availability.predictions,
        );
        // Rendered up front, not on first visit: it shares the panel cell with the Now tab, so
        // its height has to be known from the start for the widget not to grow later.
        renderWeekPanel();

        if (data.premise.chainId) {
          void renderChainPanel(
            chainPanel,
            apiOrigin,
            scriptUrl,
            data.premise.chainId,
            slug,
            locale,
          );
        }

        mountFeedbackWidget(feedbackPanel, {
          premiseId: data.premise.premiseId,
          locale,
        });

        // Guarded because the retry button can bring us back here: a second success would
        // otherwise leave two pollers running against the same panel.
        if (pollTimer === null) {
          pollTimer = setInterval(() => {
            fetchJson<PollPayload>(pollUrl())
              .then((poll) => {
                if (latestData)
                  latestData = {
                    ...latestData,
                    availability: { ...latestData.availability, ...poll },
                  };
                if (activeTab === "now")
                  nowPanel.innerHTML = renderNowTab(
                    poll,
                    forecastScale,
                    forecastBasis,
                    hours,
                    locale,
                  );
                updateCompactTotal(poll.categories, poll.predictions);
              })
              .catch((error) =>
                console.error("[ii-gp-widget] poll failed", error),
              );
          }, POLL_INTERVAL_MS);
        }
      })
      .catch((error) => {
        console.error("[ii-gp-widget] initial render failed", error);
        setLoadState("failed");
        renderLoadError();
      });
  }

  load();
}

function boot(): void {
  const script = document.currentScript as HTMLScriptElement | null;
  if (!script) {
    console.error("[ii-gp-widget] could not resolve document.currentScript");
    return;
  }
  init(script);
}

boot();
